import React from "react";
import {
  FaLinkedinIn,
  FaYoutube,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------ */
/* Content config — mirrors the real routes/URLs used in the navbar    */
/* `external: true` opens in a new tab with a plain <a>                */
/* ------------------------------------------------------------------ */
const LINK_GROUPS = [
  {
    title: "Portfolio",
    links: [
      { label: "AI Agents", to: "/portfolio/ai-agents" },
      { label: "Twilio IVRs", to: "/portfolio/twilio-ivrs" },
      { label: "Web Apps", to: "/portfolio/web-apps" },
      { label: "Mobile Apps", to: "/portfolio/mobile-apps" },
      { label: "A2P 10DLC Approval", to: "/portfolio/a2p-approval" },
      { label: "GoHighLevel", to: "/portfolio/ghl-integration" },
    ],
  },
  {
    title: "Our Product",
    links: [
      { label: "MK TextBiz", to: "https://mktextbiz.com/", external: true },
      { label: "AI Agent Demos", to: "https://agent.mkaitechnology.com/", external: true },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Home", to: "/" },
      { label: "About Us", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
  },
];

const OFFICES = [
  { country: "United States", address: "36 Paso Robles, Irvine, CA 82801" },
  { country: "United Kingdom", address: "Great Portland Street, London W1W 7NY" },
  { country: "Pakistan", address: "174 Spring North Phase 7, Bahria Town, Islamabad" },
];

const SOCIALS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/the-mk-solution/posts/?feedView=all",
    Icon: FaLinkedinIn,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCqH0bzTvg1QaApP4pWQU7Og",
    Icon: FaYoutube,
  },
];

/* ------------------------------------------------------------------ */
/* Reusable pieces                                                     */
/* ------------------------------------------------------------------ */
const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B4AFA]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0a1a]";

// Link with: colour shift, subtle slide, and an underline that grows from the left
const linkClass = `group relative inline-flex items-center gap-1.5 rounded-sm py-1 text-[14px] leading-6 text-slate-400 sm:py-0.5 transition-all duration-200 ease-out hover:translate-x-1 hover:text-white ${focusRing}`;

const Underline = () => (
  <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-gradient-to-r from-[#8B4AFA] to-[#C4B5FD] transition-transform duration-300 ease-out group-hover:scale-x-100" />
);

const FooterLink = ({ to, external, children }) =>
  external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={linkClass}>
      <span className="relative">
        {children}
        <Underline />
      </span>
      <FaExternalLinkAlt className="h-2.5 w-2.5 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
    </a>
  ) : (
    <Link to={to} className={linkClass}>
      <span className="relative">
        {children}
        <Underline />
      </span>
    </Link>
  );

const ColumnTitle = ({ children }) => (
  <p className="mb-5 text-[15px] font-semibold tracking-tight text-white">
    {children}
  </p>
);

// Icon tile used in the contact column
const IconTile = ({ children }) => (
  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-[#8B4AFA] transition-colors duration-200 group-hover:border-[#8B4AFA]/60 group-hover:bg-[#8B4AFA]/15 group-hover:text-[#C4B5FD]">
    {children}
  </span>
);

const ContactRow = ({ href, icon, children }) => (
  <a
    href={href}
    className={`group flex min-w-0 items-center gap-3 rounded-lg text-[14px] text-slate-300 transition-colors duration-200 hover:text-white ${focusRing}`}
  >
    <IconTile>{icon}</IconTile>
    <span className="min-w-0 break-words">{children}</span>
  </a>
);

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */
export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="relative mx-3 mb-3 mt-20 overflow-hidden rounded-2xl bg-black text-white sm:mx-4 sm:mb-4 lg:mx-6 lg:mb-6">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 to-purple-900/30" />
      <div className="absolute left-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

      <div className="relative mx-auto max-w-[1600px] px-6 pb-8 pt-14 sm:px-8 sm:pb-10 sm:pt-20 lg:px-12 xl:px-16">
        <div className="mb-12 grid min-w-0 grid-cols-1 gap-12 sm:mb-14 lg:grid-cols-12 lg:gap-12 xl:gap-16">
          {/* Brand */}
          <div className="min-w-0 lg:col-span-4 xl:col-span-3">
            <Link to="/" aria-label="MK AI Technologies home" className={`inline-block rounded-md ${focusRing}`}>
              <img
                src="/website-logo-footer-309.webp"
                alt="MK AI Technologies"
                width="105"
                height="57"
                loading="lazy"
                className="h-12 w-auto sm:h-14"
              />
            </Link>

            <p className="mt-6 max-w-sm text-[15px] leading-7 text-slate-300">
              We build intelligent digital products with thoughtful design,
              modern technology, and scalable solutions shaped around your
              business goals. Let&apos;s innovate together.
            </p>

            <div className="mt-7 flex gap-3">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`group flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#8B4AFA] hover:bg-[#8B4AFA] hover:shadow-lg hover:shadow-[#8B4AFA]/30 ${focusRing}`}
                >
                  <Icon className="h-4 w-4 text-slate-300 transition-colors group-hover:text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Links + contact
              Mobile: 2 cols | Tablet: 3 link cols, contact on its own row | XL: all 4 in one row */}
          <div className="grid min-w-0 grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 sm:gap-x-8 sm:gap-y-12 lg:col-span-8 xl:col-span-9 xl:grid-cols-[repeat(3,minmax(0,1fr))_minmax(0,1.9fr)]">
           {LINK_GROUPS.map((group, i) => (
              <nav
                key={group.title}
                aria-label={group.title}
                className={`min-w-0 ${i === 0 ? "row-span-2 sm:row-span-1" : ""}`}
              >
                <ColumnTitle>{group.title}</ColumnTitle>
                <ul className="space-y-2.5">
                  {group.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink to={link.to} external={link.external}>{link.label}</FooterLink>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}

            {/* Contact */}
            <div className="col-span-2 min-w-0 sm:col-span-3 xl:col-span-1">
              <ColumnTitle>Get in touch</ColumnTitle>
              <ul className="space-y-4">
                <li>
                  <ContactRow href="mailto:contact@mkaitechnology.com" icon={<FaEnvelope className="h-4 w-4" />}>
                    contact@mkaitechnology.com
                  </ContactRow>
                </li>
                <li>
                  <ContactRow href="tel:+17373438150" icon={<FaPhoneAlt className="h-4 w-4" />}>
                    +1 737-343-8150
                  </ContactRow>
                </li>

                <li className="group flex min-w-0 gap-3">
                  <span className="mt-1">
                    <IconTile>
                      <FaMapMarkerAlt className="h-4 w-4" />
                    </IconTile>
                  </span>
                  <div className="grid w-full min-w-0 grid-cols-1 gap-4">
                    {OFFICES.map(({ country, address }) => (
                      <div key={country} className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#8B4AFA]">
                          {country}
                        </p>
                        <p className="mt-0.5 text-[14px] leading-6 text-slate-300">
                          {address}
                        </p>
                      </div>
                    ))}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-7 text-center text-[13px] text-slate-400 md:flex-row md:text-left">
          <p>&copy; {year} MK AI Technologies. All rights reserved.</p>

          <div className="order-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 md:order-2">
            <Link
              to="/privacy-policy"
              className={`rounded-sm transition-colors duration-200 hover:text-white hover:underline hover:decoration-[#8B4AFA] hover:underline-offset-4 ${focusRing}`}
            >
              Privacy Policy
            </Link>
            <span aria-hidden="true" className="h-3 w-px bg-white/15" />
            <Link
              to="/terms-of-service"
              className={`rounded-sm transition-colors duration-200 hover:text-white hover:underline hover:decoration-[#8B4AFA] hover:underline-offset-4 ${focusRing}`}
            >
              Terms of Service
            </Link>
          </div>

          <p className="order-2 text-slate-500 md:order-3">
            Crafted by the MK AI Technologies team
          </p>
        </div>
      </div>
    </footer>
  );
};